// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef } from 'react';
import papers from './data/igcsepapers.json';
import PotatoGame from './PotatoGame';
import Footer from './Footer';
import matchers from '@testing-library/jest-dom/types/matchers';

export default function IGCSEPapers() {
    const availableSubjects = ['All Subjects', 'Accounting(0452)', 'Add Maths(0606)', 'Biology(0610)', 'Business(0450)', 'Chemistry(0620)', 'Computer Sci(0478)', 'Economics(0455)', 'English(0500)', 'Geography(0460)', 'History(0470)', 'Intl. Maths(0607)', 'Islamiyat(0493)', 'Mathematics(0580)', 'Physics(0625)', 'Sociology(0495)'];

    // simple hash-based routing state (hooks must be called in same order)
    const [route, setRoute] = useState<string>(window.location.hash || '#/igcsepapers');

    // Home component (minimal)
    const Home = () => (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-left p-8">
                <h1 className="text-2xl sm:text-4xl font-bold mb-2">
                    <a href="#/igcsepapers" className="text-red-600 hover:underline">• Cambridge A-Level Past Papers</a>
                </h1>
                <h1 className="text-2xl sm:text-4xl font-bold mb-2"><a href="#/edexcelalevelpapers" className="text-red-600 hover:underline">• Edexcel A-Level Past Papers</a></h1>

                <div className="mb-4">
                    <span className="text-xl sm:text-3xl font-bold mb-2 text-gray-500 italic">• (I)GCSE, AQA &amp; OCR coming soon!</span>
                </div>

                <p className="text-lg sm:text-xl text-gray-700">Choose your board!</p>
            </div>
        </div>
    );

    // routing hash listener
    useEffect(() => {
        const onHash = () => setRoute(window.location.hash || '#/igcsepapers');
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    // --- existing CambridgePapers implementation follows ---
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'year', direction: 'desc' });
    const [selectedSubject, setSelectedSubject] = useState(availableSubjects[0]);
    const [showPaperDropdown, setShowPaperDropdown] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
    const [showVariantDropdown, setShowVariantDropdown] = useState(false);
    const [selectedPapers, setSelectedPapers] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedSeasons, setSelectedSeasons] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState([]);

    // showGradeBoundaries: shown by default, persisted to localStorage
    const [showGradeBoundaries, setShowGradeBoundaries] = useState<boolean>(() => {
        try {
            const raw = window.localStorage.getItem('showGradeBoundaries');
            return raw !== null ? JSON.parse(raw) : true;
        } catch {
            return true;
        }
    });

    // refs for dropdown wrappers to detect outside clicks
    const paperRef = useRef(null);
    const yearRef = useRef(null);
    const seasonRef = useRef(null);
    const variantRef = useRef(null);

    // load data from external JSON
    const allData = useMemo(() => {
        return Array.isArray(papers) ? papers : [];
    }, []);

    const availablePapers = [1, 2, 3, 4, 5, 6, 7];
    const availableYears = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009, 2008, 2007];
    const availableSeasons = ['Spring', 'Summer', 'Winter'];
    const availableVariants = [1, 2, 3];

    // close dropdowns when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (paperRef.current && !paperRef.current.contains(e.target)) setShowPaperDropdown(false);
            if (yearRef.current && !yearRef.current.contains(e.target)) setShowYearDropdown(false);
            if (seasonRef.current && !seasonRef.current.contains(e.target)) setShowSeasonDropdown(false);
            if (variantRef.current && !variantRef.current.contains(e.target)) setShowVariantDropdown(false);
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowPaperDropdown(false);
                setShowYearDropdown(false);
                setShowSeasonDropdown(false);
                setShowVariantDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // persist the toggle
    useEffect(() => {
        try {
            window.localStorage.setItem('showGradeBoundaries', JSON.stringify(showGradeBoundaries));
        } catch {
            // ignore storage errors
        }
    }, [showGradeBoundaries]);

    // Filter data based on selections
    const filteredData = useMemo(() => {
        return allData.filter(row => {
            if (selectedSubject && selectedSubject !== 'All Subjects' && row.subject !== selectedSubject) return false;
            if (selectedPapers.length > 0 && !selectedPapers.includes(row.paper)) return false;
            if (selectedYears.length > 0 && !selectedYears.includes(row.year)) return false;
            if (selectedSeasons.length > 0 && !selectedSeasons.map(s => s.toLowerCase()).includes(row.season.toLowerCase())) return false;
            if (selectedVariants.length > 0 && !selectedVariants.includes(row.zone)) return false;
            return true;
        });
    }, [allData, selectedSubject, selectedPapers, selectedYears, selectedSeasons, selectedVariants]);

    // Sorted-data hook: use custom ordering for difficulty (Hard > Medium > Easy)
    // multi level sort: same as cie alevel
    const sortedData = useMemo(() => {
        const difficultyRank = (val: any) => {
            if (val === undefined || val === null) return 0;
            const s = String(val).trim().toLowerCase();
            if (s.includes('hard')) return 3;
            if (s.includes('medium')) return 2;
            if (s.includes('easy')) return 1;
            return 0; // unknown -> lowest priority
        };
        const seasonOrder = (season: any) => {
            const s = String(season || '').trim().toLowerCase();
            if (s.includes('spring')) return 1;
            if (s.includes('summer')) return 2;
            if (s.includes('winter')) return 3;
            return 0;
        };

        return [...filteredData].sort((a, b) => {
            // custom compare for difficulty
            // primary sort: user selected column
            if (sortConfig.key) {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                let comparison = 0;

            if (sortConfig.key === 'difficulty') {
                const ra = difficultyRank(a.difficulty);
                const rb = difficultyRank(b.difficulty);
                
                    comparison = (ra - rb) * -1; // multiply by -1 so that "desc" shows Hard -> Medium -> Easy by default
                } else {

            // if key doesn't exist on object, fallback to undefined comparison
                    if (aVal === undefined && bVal === undefined) comparison = 0;
                    else if (aVal === undefined) comparison = sortConfig.direction === 'asc' ? 1 : -1;
                    else if (bVal === undefined) comparison = sortConfig.direction === 'asc' ? -1 : 1;
                    else {
            // Handle numeric and string comparisons
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                            comparison = sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                        } else {

            // Normalize strings to compare reliably
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

                            if (aVal < bVal) comparison = sortConfig.direction === 'asc' ? -1 : 1;
                            else if (aVal > bVal) comparison = sortConfig.direction === 'asc' ? 1 : -1;
                        }
                    }
                }

                if (comparison !== 0) return comparison;
            }

            // Secondary sort: Year (descending)
            const yearDiff = (b.year ?? 0) - (a.year ?? 0);
            if (yearDiff !== 0) return yearDiff;

            // Tertiary sort: Season (Spring > Summer > Winter)
            const seasonDiff = seasonOrder(a.season) - seasonOrder(b.season);
            if (seasonDiff !== 0) return seasonDiff;

            // Quaternary sort: Paper (ascending)
            const paperDiff = (a.paper ?? 0) - (b.paper ?? 0);
            if (paperDiff !== 0) return paperDiff;

            // Quinary sort: Variant/Zone (ascending)
            const zoneDiff = (a.zone ?? 0) - (b.zone ?? 0);
            return zoneDiff;
        });
    }, [filteredData, sortConfig]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * entriesPerPage;
        const endIndex = startIndex + entriesPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, entriesPerPage]);

    const totalPages = Math.ceil(sortedData.length / entriesPerPage);
    const startEntry = sortedData.length === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
    const endEntry = Math.min(currentPage * entriesPerPage, sortedData.length);

    const handleSort = (key: string) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key: string) => {
        if (sortConfig.key !== key) return ' ↕';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const toggleCheckbox = (value: any, selectedArray: any[], setSelectedArray: any) => {
        if (selectedArray.includes(value)) {
            setSelectedArray(selectedArray.filter(item => item !== value));
        } else {
            setSelectedArray([...selectedArray, value]);
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleEntriesChange = (newEntries: number) => {
        setEntriesPerPage(newEntries);
        setCurrentPage(1); // Reset to first page
    };

    // Replace "Search" with Clear Filters: resets all filters and pagination
    const handleClearFilters = () => {
        setSelectedSubject(availableSubjects[0]);
        setSelectedPapers([]);
        setSelectedYears([]);
        setSelectedSeasons([]);
        setSelectedVariants([]);
        setShowPaperDropdown(false);
        setShowYearDropdown(false);
        setShowSeasonDropdown(false);
        setShowVariantDropdown(false);
        setCurrentPage(1);
    };

    const handleSearch = () => {
        // kept for parity (but now simply reset page). previously used as Search.
        setCurrentPage(1); // Reset to first page when searching
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages: Array<number | string> = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = Math.min(maxPagesToShow, totalPages - 1);
            }

            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - maxPagesToShow + 1);
            }

            if (startPage > 2) {
                pages.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    // helper to open link in new tab safely
    const openLink = (url?: string) => {
        if (!url) return;
        try {
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.click();
        }
    };

    // Difficulty badge component (oblong only; unified width)
    const DifficultyBadge = ({ level }: { level: string }) => {
        const value = String(level || '').trim().toLowerCase();
        const cfg: any = {
            easy: { bg: 'bg-green-500', text: 'Easy' },
            medium: { bg: 'bg-amber-500', text: 'Medium' },
            hard: { bg: 'bg-red-500', text: 'Hard' },
            default: { bg: 'bg-gray-300', text: level || 'Unknown' }
        };
        const pick = cfg[value] || cfg.default;

        return (
            <span
                role="status"
                aria-label={`Difficulty ${pick.text}`}
                className={`difficulty-badge ${pick.bg} text-white`}
            >
                <span className="difficulty-text">{pick.text}</span>
            </span>
        );
    };

    // compute dynamic colspan for empty state
    const totalVisibleColumns = showGradeBoundaries ? 6 : 3;

    // --- render: return Home if route === '#/home', otherwise full papers view ---
    if (route === '#/home') {
        return <Home />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top small nav linking to Home and other exam boards */}
            <div className="w-full bg-white border-b py-3" style={{ position: 'relative' }}>
                {/* Left-corner Potato group (positioned relative to header, so it's at left corner of site) */}
                <div className="top-nav-left" aria-hidden="false">
                    <a href="#/home" className="top-nav-link home-link">「PotatoPapers」</a>
                    { /* <span className="potato-badge" role="note">work in progress!</span> */ }
                </div>

                <div className="max-w-7xl mx-auto px-4">
                    <nav className="top-nav flex items-center justify-center" aria-label="Top navigation">
                        <div className="top-nav-center">
                            <a href="#/cambridgealevelpapers" className="top-nav-link">Cambridge AS &amp; AL</a>
                            <span className="sep">|</span>
                            <a href="#/edexcelalevelpapers" className="top-nav-link">Edexcel International AL</a>
                            <span className="sep">|</span>
                            <a href="#/igcsepapers" className="top-nav-link"> IGCSE</a>
                            <span className="sep">|</span>
                            <span className="top-nav-link muted">AQA &amp; OCR  <span className="text-gray-500 italic">Coming soon!</span></span>
                        </div>
                    </nav>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative overflow-hidden" style={{
                background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a15 100%)',
                minHeight: '460px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: '12px'
            }}>
                {/* Feedback Banner - Top Center */}
                <p className="text-white text-xs sm:text-sm opacity-90 mb-2 px-4 text-center" style={{ position: 'relative', zIndex: 50, pointerEvents: 'auto' }}>
                    🚀 Site in active development. Found a bug or have feedback? <a href="https://www.reddit.com/user/Available-Estate5153/" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-300 transition-colors">DM me on Reddit!</a>
                </p>

                {/* Potato Game Background */}
                <div className="absolute inset-0 z-0">
                   {/* <PotatoGame /> */}
                    <PotatoGame />
                </div>

                <div className="relative z-10 px-4 py-20 text-center">
                    <h1 className="text-6xl font-bold text-white mb-2 tracking-wide">
                         IGCSE Past Year Papers
                    </h1>
                    <p className="text-white text-lg italic opacity-80">Still building! Dm me and I'll reply back once it's finally released :D Go Potatoes!!! 🥔 </p>

                    {/* Search Form */}
                    <div className="max-w-6xl mx-auto mt-12 px-4">
                        <div className="flex flex-wrap justify-center gap-4 mb-6">
                            {/* Subject */}
                            <div className="text-left flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 ">Subject</label>
                                <select
                                    value={selectedSubject}
                                    onChange={(e) => setSelectedSubject(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500"
                                >
                                    {availableSubjects.map(sub => (
                                        <option key={sub} value={sub}>{sub}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Paper - Dropdown with Checkboxes */}
                            <div ref={paperRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Paper(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowPaperDropdown(!showPaperDropdown)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedPapers.length > 0 ? `${selectedPapers.length} selected` : 'PAPER'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showPaperDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availablePapers.map((paper, idx) => (
                                                <label key={paper} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availablePapers.length - 1 ? 'mb-2' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPapers.includes(paper)}
                                                        onChange={() => toggleCheckbox(paper, selectedPapers, setSelectedPapers)}
                                                        className="mr-2"
                                                    />
                                                    {paper}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Year, Season, Variant controls (unchanged) */}
                            <div ref={yearRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Year(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowYearDropdown(!showYearDropdown)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedYears.length > 0 ? `${selectedYears.length} selected` : 'YEAR'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showYearDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableYears.map((year, idx) => (
                                                <label key={year} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableYears.length - 1 ? 'mb-2' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedYears.includes(year)}
                                                        onChange={() => toggleCheckbox(year, selectedYears, setSelectedYears)}
                                                        className="mr-2"
                                                    />
                                                    {year}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div ref={seasonRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Season(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedSeasons.length > 0 ? `${selectedSeasons.length} selected` : 'SEASON'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showSeasonDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableSeasons.map((season, idx) => (
                                                <label key={season} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableSeasons.length - 1 ? 'mb-2' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSeasons.includes(season)}
                                                        onChange={() => toggleCheckbox(season, selectedSeasons, setSelectedSeasons)}
                                                        className="mr-2"
                                                    />
                                                    {season}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div ref={variantRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Variant(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowVariantDropdown(!showVariantDropdown)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedVariants.length > 0 ? `${selectedVariants.length} selected` : 'VARIANT'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showVariantDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableVariants.map((variant, idx) => (
                                                <label key={variant} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableVariants.length - 1 ? 'mb-2' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedVariants.includes(variant)}
                                                        onChange={() => toggleCheckbox(variant, selectedVariants, setSelectedVariants)}
                                                        className="mr-2"
                                                    />
                                                    {variant}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={handleClearFilters}
                                className="search-button px-16 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded transition-colors duration-200 flex items-center justify-center gap-2 text-xl"
                                aria-label="Clear filters"
                                title="Clear all filters"
                            >
                                <span className="text-xl">✖</span>
                                Clear filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Disclaimer Banner - Bottom Center */}
                <div className="w-full text-center py-2 px-4" style={{ background: 'rgba(51, 51, 51, 0.3)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <p className="text-white text-xs sm:text-sm opacity-85 max-w-4xl mx-auto">
                        Please Note: All past papers, mark schemes and examiner reports are © Cambridge Assessment International Education.
                    </p>
                </div>
            </div>

            {/* Results Section (unchanged) */}
            <div className="max-w-7xl mx-auto responsive-px py-8">
                <div className="scalable-table-section">
                    <div className="mb-4 controls-row">
                        <div className="flex items-center gap-2 controls-left">
                            <span className="text-gray-700">Show</span>
                            <select
                                value={entriesPerPage}
                                onChange={(e) => handleEntriesChange(Number(e.target.value))}
                                className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-gray-700">entries</span>
                        </div>

                        <div className="flex items-center gap-3 controls-right">
                            <label className="flex items-center gap-2 text-gray-700 select-none grade-boundaries-toggle">
                                <input
                                    type="checkbox"
                                    checked={showGradeBoundaries}
                                    onChange={(e) => setShowGradeBoundaries(e.target.checked)}
                                    className="h-4 w-4"
                                    aria-label="Show Grade Boundaries"
                                />
                                <span>Show Grade Thresholds</span>
                            </label>
                        </div>
                    </div>

                    <div className="table-container">
                    <div className="overflow-x-auto shadow-lg rounded-lg">
                        <table className="w-full bg-white pastpapers-table">
                        <thead>
                            <tr className="bg-red-600 text-white">
                                <th
                                    onClick={() => handleSort('year')}
                                    className="px-6 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base"
                                >
                                    Paper (sort by year){getSortIcon('year')}
                                </th>
                                <th
                                    onClick={() => handleSort('difficulty')}
                                    className="px-6 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base"
                                >
                                    Difficulty{getSortIcon('difficulty')}
                                </th>

                                {showGradeBoundaries && (
                                    <>
                                        <th
                                            onClick={() => handleSort('gradeA')}
                                            className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col"
                                            style={{ width: '6%' }}
                                        >
                                            A{getSortIcon('gradeA')}
                                        </th>
                                        <th
                                            onClick={() => handleSort('gradeB')}
                                            className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col"
                                            style={{ width: '6%' }}
                                        >
                                            B{getSortIcon('gradeB')}
                                        </th>
                                        <th
                                            onClick={() => handleSort('gradeC')}
                                            className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col"
                                            style={{ width: '6%' }}
                                        >
                                            C{getSortIcon('gradeC')}
                                        </th>
                                    </>
                                )}

                                <th className="px-6 py-4 text-left font-semibold text-base">Files</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan={totalVisibleColumns} className="px-6 py-8 text-center text-gray-500 text-base">
                                        No results found. Try adjusting your filters.
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map((row, index) => (
                                    <tr key={`${row.code}-${row.subject}-${row.year}-${row.season}-${row.paper}-${row.zone}`} className={`border-b border-gray-200 hover:bg-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                        <td className="px-6 py-4 text-base">{row.code}</td>
                                        <td className={`px-6 py-4 text-base`}><DifficultyBadge level={row.difficulty} /></td>

                                        {showGradeBoundaries && (
                                            <>
                                                <td className={`px-3 py-4 text-center text-base grade-boundary-col`}>{row.gradeA ?? ''}</td>
                                                <td className={`px-3 py-4 text-center text-base grade-boundary-col`}>{row.gradeB ?? ''}</td>
                                                <td className={`px-3 py-4 text-center text-base grade-boundary-col`}>{row.gradeC ?? ''}</td>
                                            </>
                                        )}
                                        <td className="px-6 py-4 files-column">
                                            <div className="center flex gap-2">
                                                <button
                                                    onClick={() => openLink(row.questionUrl)}
                                                    disabled={!row.questionUrl}
                                                    aria-label={`Open question for ${row.subject} ${row.year}`}
                                                    className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded transition-colors duration-200 font-medium ${row.questionUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}
                                                >
                                                    QP
                                                </button>

                                                <button
                                                    onClick={() => openLink(row.answerUrl)} disabled={!row.answerUrl}
                                                    aria-label={`Open answer for ${row.subject} ${row.year}`}
                                                    className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded transition-colors duration-200 font-medium ${row.answerUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}
                                                >
                                                    MS
                                                </button>

                                                <button
                                                    onClick={() => openLink(row.thresholdUrl)} disabled={!row.thresholdUrl}
                                                    aria-label={`Open examiner report for ${row.subject} ${row.year}`}
                                                    className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded transition-colors duration-200 font-medium ${row.thresholdUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}
                                                >
                                                    ER
                                                </button>

                                                {/* Video button — use background-image + cover so transparent padding in PNG is cropped away */}
                                                <button
                                                  onClick={() => row.vidUrl && openLink(row.vidUrl)}
                                                  disabled={!row.vidUrl}
                                                  aria-label={`Open video for ${row.subject} ${row.year}`}
                                                  title="Video"
                                                  className={`rounded transition-colors duration-200 flex items-center justify-center youtube-logo-button ${row.vidUrl ? '' : 'opacity-40 cursor-not-allowed'}`}
                                                  style={{ border: 'none', background: 'transparent', padding: 0, flexShrink: 0 }}
                                                >
                                                                                                    <img
                                                                                                        src="/images/youtube-logo.png"
                                                                                                        alt="YouTube"
                                                                                                        style={{ height: '36px', width: 'auto', display: 'block', maxWidth: '100%', minWidth: '36px' }}
                                                                                                    />
                                                  <span className="sr-only">Video</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                    <div className="text-gray-700 pagination-info">
                        Showing {startEntry} to {endEntry} of {sortedData.length} entries
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 border border-gray-300 rounded transition-colors duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                            Previous
                        </button>

                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className="px-4 py-2 text-gray-700">...</span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(Number(page))}
                                    className={`px-4 py-2 rounded transition-colors duration-200 ${currentPage === page ? 'bg-red-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                >
                                    {page}
                                </button>
                            )
                        ))}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 border border-gray-300 rounded transition-colors duration-200 ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
}
